import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { EMPTY_AVATAR_SRC } from 'constants/user';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import Star from 'svg/Star';
import { ButtonText } from 'components/Button';
import CustomStarRating from 'components/CustomStarRating';
import Link from 'components/Link';
import classNames from 'styles/utils/classNames';

/** Lines shown before "Read all" appears. */
const CLAMP_LINES = 3;

interface Props {
  name: string;
  /** Storage path for a Bounce user's avatar. */
  avatarPath?: string | null;
  /** Absolute URL, for imported reviews whose reviewer has no user row. */
  avatarUrl?: string | null;
  /** Preformatted by the caller — the coach and player pages format dates differently. */
  dateLabel: string;
  rating: number;
  review?: string | null;
  /** Links the avatar and name to the reviewer's profile when they have one. */
  profileHref?: string | null;
  /** Trailing badge, e.g. the coach page's On-Court/Remote pill. */
  badge?: ReactNode;
  /** Shown beside the rating in modal layout, e.g. the Google source mark. */
  sourceIcon?: ReactNode;
  /** Modal layout: the rating moves under the name and the text is never clamped. */
  inModal?: boolean;
}

/**
 * One review, shared by the coach profile, the court page and the player profile.
 * Everything that differs between them is a prop: the type pill, the external
 * source mark, and whether the reviewer's name links anywhere.
 */
export default function ReviewCard({
  name,
  avatarPath,
  avatarUrl,
  dateLabel,
  rating,
  review,
  profileHref,
  badge,
  sourceIcon,
  inModal = false,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);

  // Measured rather than estimated from the character count: line height and column
  // width both vary by breakpoint, so only layout knows if the text overflowed.
  useEffect(() => {
    if (!textRef.current) return;

    const lineHeight = parseFloat(window.getComputedStyle(textRef.current).lineHeight || '0');
    setIsClamped(textRef.current.scrollHeight > lineHeight * CLAMP_LINES);
  }, [review]);

  const ratingLabel = rating.toFixed(1);
  const avatarSrc = avatarPath
    ? getProfileImageUrlOrPlaceholder({ path: avatarPath })
    : avatarUrl || EMPTY_AVATAR_SRC;

  const avatar = (
    <img src={avatarSrc} className="h-12 w-12 shrink-0 rounded-full object-cover" alt={name} />
  );
  const nameText = <span className="typography-product-body-highlight capitalize">{name}</span>;

  return (
    <div className="flex gap-2">
      {profileHref ? (
        <Link href={profileHref} className="shrink-0">
          {avatar}
        </Link>
      ) : (
        avatar
      )}

      <div className="flex w-full min-w-0 flex-col gap-2">
        <div className="flex items-start justify-between gap-1">
          <div className="flex flex-col gap-0.5">
            {profileHref ? <Link href={profileHref}>{nameText}</Link> : nameText}
            <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
              {dateLabel}
            </span>
            {inModal && (
              <div className="mt-2 flex items-center gap-2">
                {sourceIcon}
                <CustomStarRating
                  initialRating={rating}
                  readOnly
                  gapBetweenStars="0.5"
                  starClassName="w-4 h-4"
                  unfillColorClassName="text-color-bg-lightmode-secondary"
                  fillColorClassName="text-color-text-lightmode-primary"
                />
                <span className="typography-product-body-highlight">{ratingLabel}</span>
              </div>
            )}
          </div>

          <div className={classNames('flex gap-2', inModal && 'self-end')}>
            {!inModal && (
              <span className="typography-product-body-highlight flex items-center gap-1">
                <Star className="mb-0.5 h-4 w-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                {ratingLabel}
              </span>
            )}
            {badge}
          </div>
        </div>

        {review && (
          <p
            ref={textRef}
            className={classNames(
              !inModal && !isExpanded && 'line-clamp-3',
              'mb-2 min-w-0 break-words text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
            )}
          >
            {review}
          </p>
        )}

        {!inModal && isClamped && (
          <ButtonText className="ml-auto mt-auto" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? 'Read less' : 'Read all'}
          </ButtonText>
        )}
      </div>
    </div>
  );
}
